import React, { useEffect, useState } from 'react';
import {
  BirdzDialog,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { IconButton } from '@mui/material';
import BTable from '../../../Tools/Table/BTable';
import { useTranslation } from 'react-i18next';
import { AddCircle, Edit } from '@mui/icons-material';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import ReasonForm from './ReasonForm';
import { useGetCompanies } from '../../../../hooks/datarefs';
import axios from 'axios';
import useRefresh from '../../../../hooks/useRefresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { FP2EItem } from './types';

export default function Reasons() {
  const { t } = useTranslation();
  const endpoint = '/api/bconnect/webapp/administrator/references/reason/';
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [id, setId] = useState<number | null>(null);
  const { data: clients } = useGetCompanies();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { refresh, triggerRefresh } = useRefresh();
  const { notif, notifOptions } = useNotif();

  const listFields = [
    {
      name: 'Name',
      label: t('referencesTables.technicalName')
    },
    {
      name: 'DefaultLabel',
      label: t('referencesTables.label')
    },
    {
      name: 'companies',
      label: t('referencesTables.clients'),
      transform: (value: number[]) => {
        return value.length ? `${value.length} / ${clients?.length}` : '-';
      }
    }
  ];

  const actions = [
    {
      name: 'edit',
      label: t('referencesTables.reasonActions.actions.edit'),
      render: (item: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.Id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('referencesTables.reasonActions.actions.delete'),
      render: (param: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('referencesTables.reasonActions.actions.delete'),
                content: t('referencesTables.reasonActions.actions.deleteTypeContent'),
                onValidate: () => {
                  setAction('delete');
                  axios
                    .delete(`/api/bconnect/webapp/administrator/references/reason/${param.Id}/`)
                    .then(
                      () => {
                        setAction(null);
                        triggerRefresh();
                        notif({
                          content: t('referencesTables.deleteSuccess'),
                          type: 'success'
                        });
                      },
                      () => {
                        setAction(null);
                        notif({
                          content: t('referencesTables.deleteError'),
                          type: 'success'
                        });
                      }
                    );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  return (
    <>
      <BTable
        model={'reasons'}
        endpoint={endpoint}
        fields={listFields}
        actions={actions}
        refresh={refresh}
        headerActions={[
          {
            name: t('referencesTables.reasonActions.actions.add'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />

      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('referencesTables.reasonActions.modalMessages.deleteTypeAction')
            : action === 'edit'
              ? t('referencesTables.reasonActions.modalMessages.editTypeAction')
              : t('referencesTables.reasonActions.modalMessages.createTypection')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        showCloseButton={true}
      >
        <ReasonForm
          endpoint={endpoint}
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
    </>
  );
}
